import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, MapPin, Phone, Star } from "lucide-react";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { AREAS, getAreaBySlug, getAreaProjects } from "@/data/areas";

const DOMAIN = SITE.domain;

export default function AreaPage() {
  const { slug } = useParams();
  const area = getAreaBySlug(slug);
  if (!area) return <Navigate to="/" replace />;

  const projects = getAreaProjects(area);
  const title = `Glaziers ${area.name} | Glass Balustrades, Pool Fencing & Glass Replacement | Fusion Glass`;
  const description = `Fusion Glass services the ${area.name} - frameless glass balustrades, curved and standard glass stair rails, stainless handrails, pool fencing, shower screens, mirrors and glass replacement in ${area.suburbs.slice(0, 5).join(", ")} and surrounding suburbs.`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${DOMAIN}/areas/${area.slug}#business`,
      name: "Fusion Glass",
      url: `${DOMAIN}/areas/${area.slug}`,
      telephone: "+61485032854",
      areaServed: area.suburbs.map((s) => ({ "@type": "Place", name: `${s}, NSW` })),
      parentOrganization: { "@id": `${DOMAIN}/#business` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${DOMAIN}/` },
        { "@type": "ListItem", position: 2, name: `${area.name} glaziers`, item: `${DOMAIN}/areas/${area.slug}` },
      ],
    },
  ];

  return (
    <main data-testid="area-page">
      <Seo title={title} description={description} path={`/areas/${area.slug}`} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-gradient-to-b from-brand-paper to-white pt-12 pb-16 md:pt-16 md:pb-20">
        <div className="container-x">
          <Eyebrow className="mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-brand-red" /> Service area
          </Eyebrow>
          <h1 className="max-w-3xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-4xl lg:text-5xl" data-testid="area-title">
            Glaziers in the <span className="text-brand-red">{area.name}</span>
          </h1>
          <FadeUp delay={0.2}>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">{area.blurb}</p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Fusion Glass is a family business - three installers and one admin, no subcontractors. The team that measures your
              job in the {area.name} is the team that makes it and fits it.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#area-quote" className="btn-red" data-testid="area-quote-btn">Request a free quote</a>
              <a href={`tel:${SITE.phoneMain}`} className="btn-outline" data-testid="area-call-btn">
                <Phone className="h-4 w-4 text-brand-red" /> {SITE.phoneMainDisplay}
              </a>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-xs text-slate-600"><span className="font-bold text-slate-900">5.0</span> from 27 Google reviews</p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-brand-paper py-14 md:py-16">
        <div className="container-x">
          <Eyebrow className="mb-6 block">Suburbs we cover in the {area.name}</Eyebrow>
          <div className="flex flex-wrap gap-2" data-testid="area-suburbs">
            {area.suburbs.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
                <MapPin className="h-3 w-3 text-brand-red" /> {s}
              </span>
            ))}
          </div>
          <p className="mt-5 text-xs text-slate-500">Not listed? We cover all of Greater Sydney - just ask.</p>
        </div>
      </section>

      {projects.length > 0 && (
        <section className="py-16 md:py-24" data-testid="area-projects">
          <div className="container-x">
            <Eyebrow className="mb-4 block">Recent jobs in the {area.name}</Eyebrow>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
              Work we've photographed nearby
            </h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map(({ service, project, image }, i) => (
                <FadeUp key={project.title} delay={Math.min(i * 0.06, 0.3)}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_36px_rgba(16,24,40,0.08)] transition-transform duration-300 hover:-translate-y-1"
                    data-testid={`area-project-${i}`}
                  >
                    <div className="relative overflow-hidden">
                      <img src={image} alt={`${project.title} - ${service.name}, Fusion Glass`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                      <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-red backdrop-blur-md">{service.name}</span>
                    </div>
                    <div className="flex items-start justify-between gap-4 p-5">
                      <h3 className="font-display text-sm font-extrabold leading-snug text-slate-900 sm:text-base">{project.title}</h3>
                      <ArrowUpRight className="mt-0.5 h-5 w-5 shrink-0 text-slate-400 transition-colors group-hover:text-brand-red" />
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-y border-slate-100 bg-brand-paper py-16 md:py-20">
        <div className="container-x">
          <Eyebrow className="mb-4 block">Services in the {area.name}</Eyebrow>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Everything we do, locally</h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-4 transition-colors hover:border-brand-red/40" data-testid={`area-service-${s.slug}`}>
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                <span className="text-sm font-medium text-slate-800">{s.tileName || s.name} {area.name}</span>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="area-other-areas">
            <span className="mr-1 text-xs text-slate-500">Other areas:</span>
            {AREAS.filter((a) => a.slug !== area.slug).map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="text-xs font-semibold text-slate-700 underline-offset-4 hover:text-brand-red hover:underline">{a.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" id="area-quote">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow className="mb-4 block">Free quote</Eyebrow>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Get a quote in the {area.name}</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">Tell us the suburb and what you need. We'll come out, measure and give you a clear, no-obligation price.</p>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>
    </main>
  );
}
